import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModelService } from '../../services/model.service';
import { BrandService } from '../../services/brand.service';
import { CatalogService } from '@services/data/catalog.service';
import { FormMode } from 'app/core/constants/form-mode';
import Swal from 'sweetalert2';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
    selector: 'app-model-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './model-form.component.html'
})
export class ModelFormComponent implements OnInit {
    @Input() data: any;
    @Input() existingModels: any[] = [];
    @Input() mode: FormMode = FormMode.CREATE;
    formMode = FormMode;

    form: FormGroup;
    brands: any[] = [];
    models: any[] = [];
    vehicleTypeData = [
        { type: 'suv' },
        { type: 'sedan' },
        { type: 'hatchback' },
        { type: 'van' },
        { type: 'pickup' },
    ];

    private activeModal = inject(NgbActiveModal);
    private fb = inject(FormBuilder);
    private modelService = inject(ModelService);
    private brandService = inject(BrandService);
    private catalogService = inject(CatalogService);

    public Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
    });

    constructor() {
        this.form = this.fb.group({
            code: [null, [Validators.required]],
            name: [null, [Validators.required]],
            brandCode: [null, [Validators.required]],
            vehicleType: [null, [Validators.required]],
            year: [null, [Validators.required, Validators.min(1900)]],
            isActive: [true]
        });
    }

    ngOnInit(): void {
        this.loadBrands();
        this.models = [...(this.existingModels || [])];

        if (this.data) {
            this.form.patchValue(this.data);
        }

        if (this.mode === FormMode.CREATE && !this.form.get('year')?.value) {
            this.form.patchValue({ year: new Date().getFullYear() }, { emitEvent: false });
        }

        if (this.mode === FormMode.VIEW) {
            this.form.disable();
        }

        if (this.mode === FormMode.CREATE) {
            if (this.models.length) {
                this.refreshCode();
            } else {
                this.loadModels();
            }
        }
    }

    loadBrands() {
        this.brandService.get().subscribe({
            next: (res) => this.brands = res || [],
            error: (err) => console.error(err)
        });
    }

    loadModels() {
        this.modelService.get().subscribe({
            next: (res) => {
                this.models = res || [];
                this.refreshCode();
            },
            error: (err) => console.error(err)
        });
    }

    refreshCode() {
        if (this.mode !== FormMode.CREATE) return;
        const brandCode = Number(this.form.get('brandCode')?.value);
        const year = Number(this.form.get('year')?.value);
        if (!brandCode || !year) {
            this.form.get('code')?.setValue(null, { emitEvent: false });
            return;
        }
        this.form.get('code')?.setValue(this.nextModelCode(brandCode, year), { emitEvent: false });
    }

    nextModelCode(brandCode: number, year: number): number {
        const prefix = `${Number(brandCode)}${Number(year)}`;
        let maxSeq = 0;
        for (const model of this.models) {
            if (Number(model.brandCode) !== Number(brandCode) || Number(model.year) !== Number(year)) continue;
            const raw = String(model.code ?? '');
            if (!raw.startsWith(prefix)) continue;
            const seq = Number(raw.slice(prefix.length));
            if (Number.isFinite(seq) && seq > maxSeq) maxSeq = seq;
        }
        return Number(`${prefix}${String(maxSeq + 1).padStart(2, '0')}`);
    }

    close() {
        this.activeModal.dismiss();
    }

    save() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const raw = this.form.getRawValue();
        const data = {
            ...raw,
            code: Number(raw.code),
            brandCode: Number(raw.brandCode),
            year: Number(raw.year),
            name: String(raw.name || '').trim(),
            isActive: !!raw.isActive
        };

        if (this.mode === FormMode.EDIT) {
            const key = this.data?.id ?? this.data?.code;
            if (key == null) return;
            this.modelService.update(key, data).subscribe({
                next: (res) => {
                    const saved = { ...this.data, ...data, ...(res || {}) };
                    this.catalogService.upsertModel(saved);
                    this.Toast.fire({ icon: 'success', title: 'Modelo actualizado correctamente' });
                    this.activeModal.close(saved);
                },
                error: (err) => this.showError(err, 'No se pudo actualizar el modelo')
            });
            return;
        }

        this.modelService.create(data).subscribe({
            next: (res) => {
                const saved = { ...data, ...(res || {}) };
                this.catalogService.upsertModel(saved);
                this.Toast.fire({ icon: 'success', title: 'Modelo creado correctamente' });
                this.activeModal.close(saved);
            },
            error: (err) => this.showError(err, 'No se pudo crear el modelo')
        });
    }

    private showError(err: any, fallback: string) {
        this.Toast.fire({
            icon: 'error',
            title: err?.error?.error || err?.error?.message || fallback
        });
    }
}
